import { api } from "@/modules/api";

import type {
  SearchSmartEngineRunsRequestSchema,
  SearchSmartEngineRunsResponseSchema,
  SmartEngineRunSchema,
  SmartEngineRunSummarySchema,
} from "@/domains/openapi/types";

export type SmartEngineRunSearchParams = Partial<SearchSmartEngineRunsRequestSchema>;
export type SmartEngineRunSummary = SmartEngineRunSummarySchema;

export interface PlannerActionDetail {
  action: string;
  order_index: number;
  existing_card_id?: string | null;
  planned_card_kind?: string | null;
}

export interface KeepConstraintDetail {
  card_id: string;
  required_action: string;
  anchor_note_id?: string | null;
}

export interface SmartEngineRunEvent {
  event: string;
  timestamp: string;
  space_account_id?: string | null;
  smart_view_id?: string | null;
  smart_context_id?: string | null;
  max_cards?: number | null;
  mode?: string | null;
  planned_cards?: number | null;
  filled_cards?: number | null;
  created_cards?: number | null;
  stage?: string | null;
  duration_seconds?: number | null;
  error_type?: string | null;
  error_message?: string | null;
  [key: string]: unknown;
}

export interface SmartEngineRunPayload {
  status: string;
  args: {
    space_account_id: string;
    smart_view_id: string;
    smart_context_id: string;
    mode: string;
    max_cards?: number | null;
    target_card_id?: string | null;
    context_payload?: Record<string, unknown>;
    existing_view?: {
      smart_view_id: string;
      card_count: number;
      card_ids: string[];
    } | null;
    required_keep_cards?: KeepConstraintDetail[] | null;
  };
  metrics: {
    pipeline_started: number;
    pipeline_completed: number;
    pipeline_failed: number;
    max_cards_requested: number;
    cards_planned: number;
    cards_filled: number;
    cards_created: number;
    entities_created: number;
    error_type?: string | null;
    [key: string]: unknown;
  };
  events: SmartEngineRunEvent[];
  traces?: {
    datadog_trace_id?: string | null;
    logfire_trace_id?: string | null;
    [key: string]: unknown;
  };
  result_summary?: {
    planned_cards: number;
    planner_actions: number;
    filled_cards: number;
    smart_entities: number;
    created_card_ids: string[];
    reuse_card_ids: string[];
    planner_action_details: PlannerActionDetail[];
  } | null;
  error?: {
    type: string;
    message: string;
    repr: string;
    [key: string]: unknown;
  } | null;
  timing: {
    started_at: string;
    ended_at?: string | null;
    duration_seconds?: number | null;
  };
  [key: string]: unknown;
}

export type SmartEngineRun = Omit<SmartEngineRunSchema, "payload"> & {
  payload: SmartEngineRunPayload;
};

export type SearchSmartEngineRunsResponse = SearchSmartEngineRunsResponseSchema & {
  results: SmartEngineRunSummary[];
};

const post = api.post as unknown as (
  url: string,
  options: Record<string, unknown>
) => Promise<{ data: unknown }>;

const get = api.get as unknown as (
  url: string,
  options: Record<string, unknown>
) => Promise<{ data: unknown }>;

const buildSearchBody = (
  params: SmartEngineRunSearchParams
): SearchSmartEngineRunsRequestSchema => {
  const body: SearchSmartEngineRunsRequestSchema = {
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };

  if (params.filter_by_space_account_id) {
    body.filter_by_space_account_id = params.filter_by_space_account_id;
  }
  if (params.filter_by_smart_view_id) {
    body.filter_by_smart_view_id = params.filter_by_smart_view_id;
  }
  if (params.filter_by_smart_context_id) {
    body.filter_by_smart_context_id = params.filter_by_smart_context_id;
  }
  if (params.filter_by_status) {
    body.filter_by_status = params.filter_by_status;
  }
  if (params.filter_by_mode) {
    body.filter_by_mode = params.filter_by_mode;
  }

  return body;
};

export const searchSmartEngineRuns = async (
  params: SmartEngineRunSearchParams
): Promise<SearchSmartEngineRunsResponse> => {
  const body = buildSearchBody(params);

  const resp = await post("/internal/v1/smart-engine-runs/search", {
    body,
  });

  return resp.data as SearchSmartEngineRunsResponse;
};

const transformSmartEngineRun = (raw: SmartEngineRunSchema): SmartEngineRun => ({
  ...raw,
  payload: (raw.payload ?? {}) as SmartEngineRunPayload,
});

export const getSmartEngineRun = async (smartEngineRunId: string): Promise<SmartEngineRun> => {
  const resp = await get(`/internal/v1/smart-engine-runs/${smartEngineRunId}`, {});

  return transformSmartEngineRun(resp.data as SmartEngineRunSchema);
};
